'use client';

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { Icon } from '../Icon';
import { hasTypographyRole, withoutTokens } from '../typography-role';

export type ButtonVariant =
  | 'primary'
  | 'emphasis'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'link'
  | 'action';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon' | 'charge' | 'touch' | 'touch-icon';
// Operate workflow verbs (ADR-0125) — one hue per verb, mode-invariant. Scope:
// [data-surface='pos'|'fnb'] + sanctioned workbench toolbars (convention for now;
// the operate-scope gate is Phase 2 per the ADR — not enforced by this primitive).
export type ActionVerb = 'send' | 'fire' | 'void' | 'split' | 'stage';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /**
   * Transient success micro-state (doc 11's save-state contract): swaps the
   * leading-icon slot to a checkmark for ~600ms then reverts to `leftIcon` -- pair
   * with the `useSuccessPulse` hook for the timing instead of hand-rolling a
   * `setTimeout` at the call site. Does NOT set `disabled`/`aria-busy` (a completed
   * save does not need to block interaction); `loading` wins if both are true (a
   * button cannot be mid-flight and just-completed at the same time). Also hides
   * `rightIcon` while pulsing, mirroring `loading`'s existing suppression -- keeps
   * the two "special state" icon slots symmetric instead of only `loading` clearing it.
   */
  success?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  className?: string;
  children?: ReactNode;
  /**
   * Required when `variant="action"` -- selects the workflow-verb hue (ADR-0125:
   * send=blue, fire=orange, void=red, split=slate, stage=orange/large-text-only).
   * Ignored for every other variant. Omitting it while `variant="action"` falls
   * back to `send` and logs a console.warn (mirrors the `getModuleColor` unknown-key
   * guard convention in this package).
   */
  verb?: ActionVerb | undefined;
}

const VARIANT_CLASSES: Record<Exclude<ButtonVariant, 'action'>, string> = {
  // Primary = flat canonical brand green (#238636) with a bold WHITE label (4.63:1
  // AA-normal). No gradient, glow, brightness filter, or scale transform (ADR-0114;
  // the electric --gradient-brand + dark forest ink were retired). Hover/pressed
  // shift the solid fill one/two steps darker; the focus-visible ring lives in BASE_CLASSES.
  // No size/weight override here -- SIZE_CLASSES' `text-body(-sm)-strong` roles carry
  // both for every variant uniformly (2026-09-16 modern-refresh pass); primary's
  // distinction from secondary/emphasis/etc. comes from its solid-fill color contrast,
  // not an extra font-weight step (this token set's semantic weight roles top out at
  // `strong` = 600 -- there is no sanctioned 700-equivalent role to reach for instead).
  // Resting `shadow-xs` + `active:shadow-none` (owner ruling, 2026-09-14 button-audit
  // session): 00-design-north-star.md ("flatness is a defect... shadow without a named
  // elevation level" is the anti-pattern, not shadow itself) supersedes the older
  // primitive-contract.md "never shadow-* on a button" line for solid/tonal variants --
  // reuses the same --shadow-xs tier cards already carry, no new elevation level. Still
  // no scale/translate; that stays out of scope for this pass.
  primary:
    'bg-brand-primary text-brand-primary-fg hover:bg-brand-primary-hover active:bg-brand-primary-pressed active:shadow-none shadow-xs',
  // Emphasis = tonal-green high-value SECONDARY action (pale green fill + green text).
  // The second rung of the action ladder below the solid-green primary CTA: per the
  // ADR-0114 amendment, solid green = the one primary CTA per view; tonal green = a
  // high-value secondary action (both are actions). Guaranteed-AA label via the
  // --color-brand-primary-text token (>=4.5 on -pale and -soft, both modes).
  emphasis:
    'bg-brand-primary-pale text-brand-primary-text border border-brand-primary/20 hover:bg-brand-primary-soft active:bg-brand-primary-soft active:shadow-none shadow-xs',
  // Secondary = crisp NEUTRAL action (cancel/back/dismiss). The strong border
  // (border-border-strong token, mode-aware) carries the separation; hover darkens
  // the fill. `active:` LAST, and one surface rung below hover: elevated -> sunken
  // (hover) -> deep (pressed). `--color-surface-deep` already exists, so this needs
  // no new token, and Tailwind emits `active:` after `hover:` so a held pointer
  // resolves to the pressed rung instead of being masked by hover. No scale/translate
  // (still out of scope this pass), and `:active` never fires on a disabled control,
  // so a disabled button still cannot appear pressed.
  secondary:
    'bg-surface-elevated text-primary border border-border-strong hover:bg-surface-sunken active:bg-surface-deep active:shadow-none shadow-xs',
  // Every `IconButton` is a consumer of this line -- it forces `variant="ghost"`
  // internally, so icon-only controls inherit the pressed state without naming a variant.
  // No shadow: ghost is chromeless by definition (toolbar/inline icon actions), so
  // there is no fill for a resting elevation to read against.
  ghost: 'bg-transparent text-primary hover:bg-surface-sunken active:bg-surface-deep',
  danger:
    'bg-danger text-danger-fg enabled:hover:bg-danger-text enabled:hover:text-danger-pale enabled:active:bg-danger-pale enabled:active:text-danger-text active:shadow-none shadow-xs',
  // Text-link: inline brand-primary link, underline on hover. Sizing-agnostic
  // (no button height/padding) — inherits the surrounding text size; use for
  // inline "View all" / row-action affordances. Pass className to recolor
  // (e.g. text-danger for a destructive link).
  // font-medium kept here explicitly (2026-09-16 harden pass): `link` opts out of
  // SIZE_CLASSES entirely (sizing-agnostic, see buttonClassName below), so it never
  // picked up the `text-body(-sm)-strong` roles that now carry weight for every other
  // variant -- removing BASE_CLASSES' blanket `font-medium` would have silently
  // dropped link buttons to unstyled/inherited weight (regression). No semantic role
  // exists yet for "inherit surrounding text size, but bold" -- restoring the prior,
  // already-sanctioned weight here until one does.
  link: 'bg-transparent text-brand-primary rounded-control underline-offset-2 hover:underline font-medium',
};

// Operate verb fills (ADR-0125). No `--color-action-*-hover` token exists yet, so hover/active
// darken via `brightness` filters — the simplest additive treatment until the substrate grows
// dedicated hover stops. Stage is intentionally large-text-only (3.56:1 clears AA-large only,
// not AA-normal); callers pairing verb="stage" should use size="lg" or larger bold text — not
// enforced here (the operate-scope + sizing gate is deferred, see ADR-0125 Consequences).
const ACTION_VARIANT_CLASSES: Record<ActionVerb, string> = {
  send: 'bg-action-send text-action-send-fg hover:brightness-95 active:brightness-90',
  fire: 'bg-action-fire text-action-fire-fg hover:brightness-95 active:brightness-90',
  void: 'bg-action-void text-action-void-fg hover:brightness-95 active:brightness-90',
  split: 'bg-action-split text-action-split-fg hover:brightness-95 active:brightness-90',
  stage: 'bg-action-stage text-action-stage-fg hover:brightness-95 active:brightness-90',
};

function resolveActionVerb(verb: ActionVerb | undefined): ActionVerb {
  if (verb) return verb;
  // Dev-gated console.warn (2026-09-14 button-audit session) — mirrors the
  // getModuleColor unknown-key convention in this package (surfaces the programmer
  // error rather than failing silently), but no longer fires in production builds.
  if (process.env['NODE_ENV'] !== 'production') {
    console.warn(
      '[Button] variant="action" requires a `verb` prop (send|fire|void|split|stage) — falling back to "send".',
    );
  }
  return 'send';
}

const SIZE_CLASSES: Record<ButtonSize, string> = {
  // De-pilled 2026-07-02 (ADR-0127): back to an 8px rectangle (rounded-control) at
  // 32/40/48px heights — the 2026-06-27 rounded-full pill treatment is retired.
  // Icon-only (below) already used rounded-control and is unchanged.
  // sm/md/lg heights now read `--button-height-*` (2026-09-14 button-audit session)
  // instead of hardcoded h-8/h-10/h-12 -- same values (32/40/48px) in the default
  // scope, but this makes `[data-density='compact']`'s own override of those same
  // tokens (tokens.css) actually take effect for the first time; `icon` and `charge`
  // already followed this token-driven pattern (--h-chrome / --h-btn-charge) below.
  // text-body-sm -> text-body-sm-strong (2026-09-16 modern-refresh pass): the
  // `-strong` role carries the same size but a 600 weight (up from body text's
  // 400/500), replacing the raw font-weight utility BASE_CLASSES used to carry.
  //
  // -> text-control-label-sm (2026-09-19, owner ruling, Typography v4 PR 3):
  // SAME size, SAME 600 weight, different token. `--text-body(-sm)-strong-weight`
  // moved to 560 in PR 3 for PROSE emphasis, which would have silently un-bolded
  // every button in the product -- BASE_CLASSES carries no `font-*` utility, so
  // these size classes are the only thing setting weight. The control-label role
  // keeps 600 for control labels while body emphasis reads quieter. Asserted by
  // computed style in tests/visual-regression/button-weight.spec.ts, because a
  // 40-unit weight shift is invisible to the screenshot suite's default
  // pixelmatch threshold (that is exactly how the regression went unnoticed).
  sm: 'h-[var(--button-height-sm)] px-4 text-control-label-sm rounded-control',
  // Touch = 44px accessibility floor for touch-capable operator surfaces (dining/host,
  // POS-adjacent backoffice) that are too dense for `lg` (48px) but must clear the
  // documented touch-target minimum (docs/design-system/.../12-accessibility-rules.md:
  // "apply the same 44px floor as general guidance on touch-capable backoffice surfaces").
  // Formalizes what call sites were previously hand-rolling as `size="sm"` + a
  // `min-h-[44px]` className override (the override silently won, so this tier changes
  // no rendered pixels -- it only stops the size prop and the className from disagreeing).
  touch: 'h-11 px-4 text-control-label-sm rounded-control',
  // Touch-icon = the SQUARE sibling of `touch`: a 44px accessibility floor
  // for an ICON-ONLY control, where `touch`'s own `px-4` (built for a text
  // label) resolves to 48px wide against a single 16px icon rather than an
  // exact 44 (VT P5 acceptance review, 05-implementation-session-plan.md).
  // `p-0` deliberately, matching `icon`'s own shape -- this is `icon`'s
  // 32px chrome square widened to the 44px touch floor, not `touch` with its
  // padding zeroed. Existing call sites that hand-rolled `size="touch"` +
  // `!h-11 !w-11 !p-0` should migrate to this tier instead: the `!important`
  // overrides were reinstating the exact disagreement `touch` itself exists
  // to stop (see that tier's own comment) -- a new tier is the structural
  // fix; `!important` was the accidental one.
  'touch-icon': 'h-11 w-11 p-0 rounded-control',
  md: 'h-[var(--button-height-md)] px-5 text-control-label rounded-control',
  // `text-body` added (2026-09-14 button-audit session) -- lg previously had no
  // text-size class at all, so a 48px button silently inherited whatever ambient
  // font-size surrounded it instead of the documented body scale. text-body ->
  // text-body-strong (2026-09-16 modern-refresh pass) -> text-control-label
  // (2026-09-19 owner ruling) -- see sm's comment above for both moves.
  lg: 'h-[var(--button-height-lg)] px-6 text-control-label rounded-control',
  // Icon-only: 32 px chrome square (--h-chrome); designed for icon buttons and compact
  // toolbar actions. p-0 + rounded-control matches sibling button radii in the same row.
  icon: 'h-[var(--h-chrome)] w-[var(--h-chrome)] p-0 rounded-control',
  // Charge = full-width hero CTA (payment / action confirm). --h-btn-charge is the tallest
  // tier (56 px); rounded-card = 16 px gives it a card-level curvature to stand out.
  // font-medium kept here explicitly (2026-09-16 harden pass): charge uses the raw
  // `text-base` size utility, not a `text-body(-sm)-strong` role, so it never picked up
  // this pass's weight bump -- removing BASE_CLASSES' blanket `font-medium` would have
  // silently dropped every payment/checkout CTA (ConfirmTotal, ReviewScreen,
  // PaymentScreen, MakePayment, etc.) to unstyled/inherited weight (regression).
  // Restoring the prior, already-sanctioned weight here until `text-base` gets a
  // `-strong` sibling of its own.
  charge: 'h-[var(--h-btn-charge)] w-full rounded-card text-base font-medium',
};

const BASE_CLASSES =
  // shrink-0 + whitespace-nowrap: a button is an atomic control — its label must
  // never wrap or be crushed when it sits in a tight flex row (e.g. input + button).
  // Fixed 2026-09-14 (button-audit session): the old `transition-[colors,transform,box-shadow]`
  // was a no-op for color -- `colors` is not a real CSS property (the Tailwind utility
  // for that is `transition-colors`, which expands to an actual property list); inside
  // an arbitrary-value transition-[...] list it's passed through literally and dropped
  // as invalid, so this button was the only primitive in the package whose hover/active
  // color changes never animated (12 siblings correctly use `transition-colors`).
  // Named properties + the `--duration-fast`/`--easing-standard` tokens (DESIGN.md
  // Motion) now also cover the `shadow-xs`/`active:shadow-none` elevation above.
  // No raw font-weight utility here (2026-09-16, modern-refresh crisping pass,
  // owner ruling): weight now comes from the `text-body(-sm)-strong` semantic
  // roles in SIZE_CLASSES below (600, up from the prior `font-medium` 500) --
  // never a raw `font-*` utility (conv-design-context.md: "semantic type tokens
  // only -- never raw text-2xl/font-bold/font-semibold").
  'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap ' +
  'transition-[background-color,border-color,color,box-shadow] duration-[var(--duration-fast)] ease-[var(--easing-standard)] ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface ' +
  'disabled:opacity-disabled disabled:cursor-not-allowed';

// A solid or ghost button is told apart by its fill only, and Windows high-contrast mode drops fills, so the
// system draws a border instead (P-9 F14). Variants that already carry a border are unchanged, and the inline
// `link` variant is text, not a box, so it gets none. A no-op outside forced-colors mode.
const FORCED_COLORS_BORDER = 'forced-colors:border';

// The label typography SIZE_CLASSES / VARIANT_CLASSES contribute: the control-label
// roles, charge's raw `text-base`, and the `font-medium` that charge and link carry.
const OWN_TYPOGRAPHY: ReadonlySet<string> = new Set([
  'text-control-label',
  'text-control-label-sm',
  'text-base',
  'font-medium',
]);

// Shared class builder so non-<button> CTAs (e.g. a TanStack Router <Link>) adopt the exact
// Button look instead of hand-rolling brand classes (h-8 bg-success etc.). The Button below
// uses it internally; link/anchor CTAs use: className={buttonClassName({ variant: 'primary' })}.
export function buttonClassName(
  opts: {
    variant?: ButtonVariant;
    size?: ButtonSize;
    verb?: ActionVerb | undefined;
    className?: string | undefined;
  } = {},
): string {
  const { variant = 'primary', size = 'md', verb, className } = opts;
  // link is sizing-agnostic (inherits surrounding text size; no control height).
  let sizeCls = variant === 'link' ? '' : SIZE_CLASSES[size];
  let variantCls =
    variant === 'action'
      ? ACTION_VARIANT_CLASSES[resolveActionVerb(verb)]
      : VARIANT_CLASSES[variant];
  // A caller typography role (e.g. `text-caption`) replaces this button's own
  // label typography; height, padding and radius are kept (#13460). Without
  // this the element carried two size roles and the stylesheet order, not the
  // caller, decided the label. No role in className = byte-identical output.
  if (hasTypographyRole(className)) {
    sizeCls = withoutTokens(sizeCls, OWN_TYPOGRAPHY);
    variantCls = withoutTokens(variantCls, OWN_TYPOGRAPHY);
  }
  return [
    BASE_CLASSES,
    variant === 'link' ? '' : FORCED_COLORS_BORDER,
    variantCls,
    sizeCls,
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    success = false,
    leftIcon,
    rightIcon,
    className,
    children,
    disabled,
    type = 'button',
    verb,
    ...rest
  },
  ref,
) {
  const classes = buttonClassName({ variant, size, verb, className });
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={classes}
      {...rest}
    >
      {loading ? <Spinner /> : success ? <SuccessIcon /> : leftIcon}
      {children}
      {!loading && !success && rightIcon}
    </button>
  );
});

function Spinner() {
  // lucide Loader2 + animate-spin — sized via the --icon-* scale (sm = 16px = the old h-4 w-4).
  return <Icon as={Loader2} size="sm" className="animate-spin" />;
}

function SuccessIcon() {
  // lucide Check — mirrors Spinner's shape exactly. No color override: the icon
  // inherits currentColor from the button's variant, same as leftIcon always has.
  return <Icon as={Check} size="sm" />;
}
